import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Plus, ArrowLeftRight, Users, Inbox, Share2, Eye, Loader2 } from 'lucide-react';
import { getMyFamily, getFamilyRequests, switchProfile } from '../../redux/familySlice';
import { getFileURL } from '../../utils/fileURL';
import AddFamilyMember from './AddFamilyMember';
import SwitchProfile from './SwitchProfile';
import FamilyRequests from './FamilyRequests';
import SharingSettings from './SharingSettings';

// The family control in the HEADER: overlapping avatars and "N accounts",
// opening a small menu with Add and Switch.
//
// It lives here rather than in the sidebar because it answers "who am I signed
// in as", which belongs beside the identity at the top of the screen and stays
// visible when the sidebar is collapsed.
//
// Renders nothing for a patient with no family when the feature is off, so an
// ordinary single patient sees no change.
export default function FamilyAccountMenu() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { primary, members, enabled, activePatientId, maxMembers, requestsPending } =
        useSelector((s) => s.family || {});

    const [open, setOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [switchOpen, setSwitchOpen] = useState(false);
    const [reqOpen, setReqOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [switching, setSwitching] = useState(null);
    const boxRef = useRef(null);

    useEffect(() => {
        dispatch(getMyFamily());
        // Fetched on mount, not when the menu opens: an unanswered request has
        // to be visible as a count on the closed control, or the only way to
        // discover it would be to go looking for it.
        dispatch(getFamilyRequests());
    }, [dispatch]);

    useEffect(() => {
        if (!open) return undefined;
        const away = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', away);
        return () => document.removeEventListener('mousedown', away);
    }, [open]);

    const people = [primary, ...(members || [])].filter(Boolean);
    const hasFamily = people.length > 1;
    const pendingCount = (requestsPending || []).length;

    // The two or three profiles this person actually moves between, most recent
    // first, with anyone they no longer have access to dropped. Switching is a
    // routine action taken many times a day; making it cost a modal every time
    // was the wrong trade, and a family of eight turns that modal into a scroll.
    const recentIds = (() => {
        try { return JSON.parse(localStorage.getItem('recentProfiles') || '[]'); }
        catch { return []; }
    })();
    const others = people.filter((p) => p.patientId !== activePatientId);
    const recent = [
        ...recentIds.map((id) => others.find((p) => p.patientId === id)).filter(Boolean),
        ...others.filter((p) => !recentIds.includes(p.patientId)),
    ].slice(0, 3);

    const quickSwitch = async (patientId) => {
        setSwitching(patientId);
        try {
            await dispatch(switchProfile(patientId)).unwrap();
            window.location.reload();
        } catch {
            setSwitching(null);
        }
    };
    const atLimit = (members || []).length >= (maxMembers || 10);

    if (!enabled && !hasFamily && pendingCount === 0) return null;

    const active = people.find((p) => p.patientId === activePatientId) || primary;
    const activeName = [active?.name, active?.surname].filter(Boolean).join(' ').trim();
    // Show the person you are ACTING AS first — the face you expect to see.
    const ordered = [active, ...people.filter((p) => p?.patientId !== active?.patientId)].filter(Boolean);

    const avatar = (p, i) => (
        <span
            key={p.patientId}
            className="-ml-2 first:ml-0 inline-block h-7 w-7 overflow-hidden rounded-full border-2 border-white bg-[#4ABDBD]/15 ring-0"
            style={{ zIndex: ordered.length - i }}
        >
            {p.image || p.imagePath ? (
                <img src={getFileURL(p.image || p.imagePath)} alt="" className="h-full w-full object-cover" />
            ) : (
                <span className="flex h-full w-full items-center justify-center text-[10px] font-bold text-[#3AA8A8]">
                    {(p.name || '?').charAt(0).toUpperCase()}
                </span>
            )}
        </span>
    );

    return (
        <div className="relative" ref={boxRef}>
            <button
                type="button"
                data-testid="family-account-menu"
                onClick={() => setOpen((v) => !v)}
                title={activeName}
                className="relative flex cursor-pointer items-center gap-2 rounded-full border border-gray-200 bg-white py-1 pl-1 pr-3 transition hover:shadow-sm"
            >
                {pendingCount > 0 && (
                    <span
                        data-testid="family-request-badge"
                        className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
                    >
                        {pendingCount}
                    </span>
                )}
                <span className="flex items-center">
                    {/* Four is enough to read as a group without crowding the bar. */}
                    {ordered.slice(0, 4).map(avatar)}
                </span>
                <span className="hidden text-xs font-semibold text-gray-700 sm:inline">
                    {people.length} {people.length === 1
                        ? t('family.account_one', 'account')
                        : t('family.account_other', 'accounts')}
                </span>
            </button>

            {open && (
                <div
                    data-testid="family-account-dropdown"
                    className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
                >
                    {/* Who you are acting as. With a profile switcher this is the
                        thing people lose track of, and booking on the wrong
                        profile means the wrong medical record. */}
                    <div className="border-b border-gray-100 px-3 py-2">
                        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                            <Users className="h-3 w-3" />
                            {t('family.title', 'Family')}
                        </p>
                        <p className="mt-0.5 truncate text-xs font-semibold text-gray-800">{activeName}</p>
                        <p className="text-[11px] text-gray-500">
                            {active && !active.primaryPatientId
                                ? t('family.primary', 'Primary')
                                : (active?.relationship
                                    ? t(`family.rel.${String(active.relationship).toLowerCase()}`, active.relationship)
                                    : t('family.member', 'Member'))}
                        </p>
                    </div>

                    {enabled && (
                        <button
                            type="button"
                            data-testid="family-add-button"
                            disabled={atLimit}
                            onClick={() => { setOpen(false); setAddOpen(true); }}
                            title={atLimit ? t('family.limit_reached', 'Family member limit reached') : undefined}
                            className="flex w-full cursor-pointer items-center gap-2 px-3 py-2.5 text-left text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <Plus className="h-3.5 w-3.5 text-[#4ABDBD]" />
                            {t('family.add_secondary', 'Add secondary patient')}
                        </button>
                    )}

                    {/* Requests. Always shown when something is waiting; the
                        History tab behind it is how both sides can see what was
                        asked and answered, so it stays reachable at zero too. */}
                    <button
                        type="button"
                        data-testid="family-requests-button"
                        onClick={() => { setOpen(false); setReqOpen(true); }}
                        className="flex w-full cursor-pointer items-center gap-2 border-t border-gray-100 px-3 py-2.5 text-left text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
                    >
                        <Inbox className="h-3.5 w-3.5 text-[#4ABDBD]" />
                        {t('family.requests', 'Requests')}
                        {pendingCount > 0 && (
                            <span className="ml-auto rounded-full bg-red-500 px-1.5 py-px text-[10px] font-bold text-white">
                                {pendingCount}
                            </span>
                        )}
                    </button>

                    {/* The owner's own screen. Always reachable, even with
                        nothing shared — it is where you go to check who can
                        open your account, and "nobody" is an answer worth
                        being able to confirm. */}
                    <button
                        type="button"
                        data-testid="family-sharing-button"
                        onClick={() => { setOpen(false); setShareOpen(true); }}
                        className="flex w-full cursor-pointer items-center gap-2 border-t border-gray-100 px-3 py-2.5 text-left text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
                    >
                        <Share2 className="h-3.5 w-3.5 text-[#4ABDBD]" />
                        {t('family.sharing', 'Sharing')}
                    </button>

                    {/* Shown whenever there is more than one person, even with the
                        feature off: an existing family must not lose access to
                        profiles it already has. */}
                    {hasFamily && (
                        <div className="border-t border-gray-100 pt-1.5">
                            <p className="flex items-center gap-1.5 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                <ArrowLeftRight className="h-3 w-3" />
                                {t('family.switch_profile', 'Switch profile')}
                            </p>

                            {recent.map((p) => (
                                <button
                                    key={p.patientId}
                                    type="button"
                                    data-testid={`quick-switch-${p.patientId}`}
                                    disabled={!!switching}
                                    onClick={() => quickSwitch(p.patientId)}
                                    className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left transition hover:bg-gray-50 disabled:opacity-60"
                                >
                                    {p.image || p.imagePath ? (
                                        <img src={getFileURL(p.image || p.imagePath)} alt=""
                                            className="h-6 w-6 flex-shrink-0 rounded-full object-cover" />
                                    ) : (
                                        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#4ABDBD]/15 text-[10px] font-bold text-[#3AA8A8]">
                                            {(p.name || '?').charAt(0).toUpperCase()}
                                        </span>
                                    )}
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-xs font-semibold text-gray-700">
                                            {[p.name, p.surname].filter(Boolean).join(' ').trim()}
                                        </span>
                                        <span className="block text-[10px] text-gray-400">
                                            {p.accountType === 'secondary'
                                                ? t('family.secondary', 'Secondary')
                                                : t('family.primary', 'Primary')}
                                        </span>
                                    </span>
                                    {/* Read-only is worth knowing BEFORE switching,
                                        not after a button fails. */}
                                    {switching === p.patientId
                                        ? <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-[#4ABDBD]" />
                                        : p.permission === 'read' && (
                                            <Eye className="h-3 w-3 flex-shrink-0 text-gray-400"
                                                 title={t('family.read_only', 'Read only')} />
                                        )}
                                </button>
                            ))}

                            {/* ALWAYS SHOWN, even when the shortlist already
                                covers everyone.
                                
                                It was hidden when there was nothing extra to
                                list, on the reasoning that a link repeating the
                                same names does nothing. That was wrong: the full
                                screen is not just a longer list, it is the only
                                route to Edit -> Split and Leave. Hiding it took
                                those actions away entirely from anyone with
                                three accounts or fewer. */}
                            <button
                                type="button"
                                data-testid="family-switch-button"
                                onClick={() => { setOpen(false); setSwitchOpen(true); }}
                                className="w-full cursor-pointer px-3 py-2 text-left text-[11px] font-semibold text-[#3AA8A8] transition hover:bg-gray-50"
                            >
                                {t('family.see_more', 'See all')} ({others.length})
                            </button>
                        </div>
                    )}
                </div>
            )}

            <AddFamilyMember isOpen={addOpen} onClose={() => setAddOpen(false)} />
            <SwitchProfile isOpen={switchOpen} onClose={() => setSwitchOpen(false)} />
            <FamilyRequests isOpen={reqOpen} onClose={() => setReqOpen(false)} />
            <SharingSettings isOpen={shareOpen} onClose={() => setShareOpen(false)} />
        </div>
    );
}
